import React, { useMemo, useState, useEffect } from 'react';
import { Collapse, Tooltip, Icon } from 'antd';
import { Button, Table, Widget, ColumnProps, ColumnType, ButtonDelete, Row, Col, Form, FormInput, FormResetButton } from '@digital/ui';
import { compareDates, compareNumber, compareNumberString, compareText } from '@app/helpers/comparators';
import { deleteSasFornecedorPainel, getAllSasFornecedorPainel, sasFornecedorPainel } from '@app/api/sas/sasFornecedor';
const { Panel } = Collapse;
import { hasPermission } from '@app/helpers/permissions';
import StatusCircleTresCores from '@app/components/StatusCircle/statusCircleTresCores';
import moment from 'moment';

const TabelaFornecedores = () => {
  const [allFornecedor, setFornecedor] = useState<sasFornecedorPainel[]>();

  useEffect(() => {
    getAll();
  }, []);

  const getAll = async () => {
    const resultFornecedorGetAll = await getAllSasFornecedorPainel();
    setFornecedor(resultFornecedorGetAll);
  };

  const filterDropdown = (setSelectedKeys, selectedKeys, confirm, clearFilters, placeholder) => {
    return (
      <Form>
        <Row>
          <FormInput
            placeholder={placeholder}
            name="nome"
            style={{ marginTop: '15px' }}
            onChange={e => (setSelectedKeys ? setSelectedKeys(e.target.value ? [e.target.value] : []) : () => { })}
            onPressEnter={() => {
              if (typeof confirm === 'function') confirm();
            }}
          />
        </Row>
        <Row>
          <Col span={6}>
            <Button
              type="primary"
              style={{ marginLeft: '5px' }}
              onClick={() => {
                if (typeof confirm === 'function') confirm();
              }}
              icon="commons-search"
              size="small">
              Pesquisar
            </Button>
          </Col>
          <Col span={6}>
            <FormResetButton
              size="small"
              style={{ float: 'right', marginRight: '5px', marginBottom: '15px' }}
              beforeReset={() => {
                if (typeof clearFilters === 'function') clearFilters(selectedKeys || []);
              }}>
              Limpar
            </FormResetButton>
          </Col>
        </Row>
      </Form>
    )
  }

  const columns = useMemo(() => {
    const columns: ColumnProps<sasFornecedorPainel>[] = ([] = new Array());

    columns.push({
      title: 'Fornecedor',
      dataIndex: 'codigoTotvs',
      width: '8%',
      sorter: (a, b) => compareNumberString(a.codigoTotvs, b.codigoTotvs),
      filterIcon: filtered => {
        const classes = filtered ? 'anticon-filter ant-table-filter-selected' : '';
        return <Icon type="search" className={classes} style={{ minWidth: 'auto', border: 'none' }} />;
      },
      onFilter: (value, record) => record.codigoTotvs && record.codigoTotvs.toString().toLowerCase().includes(value.toLowerCase()),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => filterDropdown(setSelectedKeys, selectedKeys, confirm, clearFilters, 'Pesquisar Fornecedor')
    });

    columns.push({
      title: 'CNPJ',
      dataIndex: 'cnpj',
      width: '8%',
      sorter: (a, b) => compareNumberString(a.cnpj, b.cnpj),
      filterIcon: filtered => {
        const classes = filtered ? 'anticon-filter ant-table-filter-selected' : '';
        return <Icon type="search" className={classes} style={{ minWidth: 'auto', border: 'none' }} />;
      },
      onFilter: (value, record) => record.cnpj && record.cnpj.toString().toLowerCase().includes(value.toLowerCase()),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => filterDropdown(setSelectedKeys, selectedKeys, confirm, clearFilters, 'Pesquisar CNPJ')
    });

    columns.push({
      title: 'Razão Social',
      dataIndex: 'razaoSocial',
      sorter: (a, b) => compareText(a.razaoSocial, b.razaoSocial),
      filterIcon: filtered => {
        const classes = filtered ? 'anticon-filter ant-table-filter-selected' : '';
        return <Icon type="search" className={classes} style={{ minWidth: 'auto', border: 'none' }} />;
      },
      onFilter: (value, record) => record.razaoSocial && record.razaoSocial.toString().toLowerCase().includes(value.toLowerCase()),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => filterDropdown(setSelectedKeys, selectedKeys, confirm, clearFilters, 'Pesquisar Razão Social')
    });

    columns.push({
      title: 'Relatório Financeiro',
      width: '10%',
      type: ColumnType.Date,
      render: (text, record, index) => {
        return (
          <>
            <StatusCircleTresCores dataFormatada={record.confideFormat} dataInicial={record.confide} intermediario={60} />
          </>
        );
      },
      sorter: (a, b) => compareDates(a.confide, b.confide),
    });
    columns.push({
      title: 'Avaliação Financeira',
      width: '10%',
      type: ColumnType.Date,
      render: (text, record, index) => {
        return <StatusCircleTresCores dataFormatada={record.financeiroFormat} dataInicial={record.financeiro} intermediario={60} />;
      },
      sorter: (a, b) => compareDates(a.financeiro, b.financeiro),
    });
    columns.push({
      title: 'Compliance',
      dataIndex: 'compliace',
      width: '10%',
      type: ColumnType.Date,
      render: (text, record, index) => {
        return <StatusCircleTresCores dataFormatada={record.compliaceFormat} dataInicial={record.compliace} intermediario={60} />;
      },
      sorter: (a, b) => compareDates(a.compliace, b.compliace),
    });

    columns.push({
      title: 'Ações',
      width: '8%',
      render: (text, record, index) => {
        return getAllButtons(text, record, index);
      },
    });

    return columns;
  }, [allFornecedor]);

  const getAllButtons = (text: string, record: sasFornecedorPainel, index: number) => {
    const itens = [];

    if (hasPermission(['SAS_FORNECEDOR_EXCLUIR'])) {
      itens.push(
        <Tooltip title="Deletar Item">
          <ButtonDelete
            icon="science-minus"
            type="link"
            confirm="Deseja realmente excluir este registro?"
            okText="Confirmar"
            onClick={() => {
              deleteSasFornecedorPainel(record.id).then(() => {
                getAll();
              });
            }}
            className="btn-delete-without-text"
          />
        </Tooltip>,
      );
    }

    itens.push(
      <Tooltip title="Ir para Fornecedor">
        <Button
          key="editItem"
          icon="arrows-circled_right_2"
          onClick={() => {
            window.location.href = `./painelFinanceiroformFornecedor?id=${record.id}&editarItem=true`;
          }}></Button>
      </Tooltip>,
    );

    return itens;
  };

  const btnPainelFinanceiroFornecedor = () => {
    const itens = [];
    if (hasPermission(['SAS_FORNECEDOR_INCLUIR'])) {
      itens.push(
        <Button
          onClick={() => {
            window.location.href = `./painelFinanceiroformFornecedor`;
          }}>
          Incluir Fornecedor
        </Button>,
      );
    }
    return itens;
  };

  return (
    <Widget title="Painel Financeiro dos Fornecedores" actions={btnPainelFinanceiroFornecedor()}>
      <Row style={{ display: 'flex', gap: '16px' }}>
        <StatusCircleTresCores intermediario={60} dataFormatada='Vigente' dataInicial={moment().add(90, 'days').toDate()} />
        <StatusCircleTresCores intermediario={60} dataFormatada='Menos de 60 dias para o vencimento' dataInicial={moment().add(30, 'days').toDate()} />
        <StatusCircleTresCores intermediario={60} dataFormatada='Vencido' dataInicial={moment().add(0, 'days').toDate()} />
      </Row>
      <Table rowKey="id" columns={columns} dataSource={allFornecedor} />
    </Widget>
  );
};

export default TabelaFornecedores;
